import { faSitemap } from "@fortawesome/pro-regular-svg-icons"
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material"
import { translate } from "app/language/service"
import { getActiveAppContext } from "app/tools/common"
import { formEventModule } from "form/event"
import { FORM_TAB_BOXES, IForm } from "form/model"
import { onConfigurationFormChange } from "form/tools/configuration"
import { noticeAndClearSspDealsError, refreshSSPDealList } from "form/tools/ssp-deal"
import React from "react"
import { getAllSspsInStore } from "ssp/tools/common"
import { FlexBox } from "ui/components/FlexBox"
import { TestHiddenData } from "ui/components/TestHiddenData"
import { ScreensBoxLayout } from "./ScreensBoxLayout"
import { SspSspDealRow } from "./SspSspDealRow"

interface IContentSspDealsProps {
  form: IForm
}

export const ContentSspDeals = React.memo(({ form }: IContentSspDealsProps): JSX.Element => {
  const [rowRenderIndex, setRowRenderIndex] = React.useState(0)
  const isNoticingSspDealsError = React.useRef(false)
  const selectedSspIds = form.screens.sspIds
  const sspError = form.context.screens.errors.sspIds
  React.useEffect(() => {
    if (!getActiveAppContext()) {
      return
    }
    refreshSSPDealList(form.context.appContext, selectedSspIds)
  }, [getActiveAppContext()])
  React.useEffect(() => {
    if (!getActiveAppContext()) {
      return
    }
    if (form.context.relations.sspDealsError) {
      if (!isNoticingSspDealsError.current) {
        isNoticingSspDealsError.current = true
        noticeAndClearSspDealsError(form)
        setRowRenderIndex(rowRenderIndex + 1)
      }
    } else if (isNoticingSspDealsError.current) {
      isNoticingSspDealsError.current = false
    }
    formEventModule.on.sspSelectionChanged("component-ContentSspDeals", newSSPSelectedIds =>
      refreshSSPDealList(form.context.appContext, newSSPSelectedIds)
    )
    return () => formEventModule.remove("component-ContentSspDeals")
  }, [form.screens])
  const selectedSsps = getAllSspsInStore().filter(ssp => selectedSspIds.includes(ssp.id))
  const isSynchronizing = form.screens.isSynchronizingSsp || form.screens.isSynchronizingSspDeal
  const descriptions = [translate("campaign.ssp-deal.description")]
  return (
    <ScreensBoxLayout
      id={FORM_TAB_BOXES.SCREENS_SSP_DEALS}
      title={translate("ssp-deals.title")}
      isLoading={isSynchronizing}
      icon={faSitemap}
      sxChildren={{
        "& > *": {
          width: "100%"
        },
        "& .MuiTableCell-root": {
          fontWeight: 300,
          fontSize: ".85rem"
        },
        "& .MuiTableCell-head, & .MuiTableCell-root": {
          pt: ".4rem",
          pb: ".4rem"
        }
      }}>
      <FlexBox vertical ai="start" jc="start">
        <TestHiddenData id={form.screens.isLoadingAllSspDeals ? "ssp-deal-is-loading" : "ssp-deal-is-loaded"} />
        {isSynchronizing && <TestHiddenData id="ssp-deal-is-synchronizing" />}
        {!isSynchronizing && <TestHiddenData id="ssp-deal-synchronized" />}
        {descriptions.map((description, index) => (
          <Typography color="textSecondary" fontSize=".85rem" key={index}>
            {description}
          </Typography>
        ))}
        {form.screens.allSspDeals.length === 0 ? null : (
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  id="ssp-deal-bid-only-on-deals"
                  checked={form.configuration.main.isBiddingOnlyOnDeals}
                  onChange={(_event, checked) => {
                    onConfigurationFormChange(form, {
                      ...form.configuration,
                      main: { ...form.configuration.main, isBiddingOnlyOnDeals: checked }
                    })
                  }}
                />
              }
              disabled={form.context.isSavingTempCampaign}
              label={translate("campaign.ssp-deal.bid.only.on.deal")}
            />
          </FormGroup>
        )}
      </FlexBox>
      {sspError && (
        <Typography color="error" fontSize=".9rem" minHeight="1.5rem" sx={{ mt: "1rem" }}>
          {translate("ssp.error")}
        </Typography>
      )}
      {form.screens.allSspDeals.length === 0 ? (
        <Typography color="textPrimary" fontSize=".9rem" sx={{ mt: "1rem" }}>
          {translate("campaign.ssp-deal.not.available")}
        </Typography>
      ) : (
        <TableContainer sx={{ mt: "1rem" }}>
          <TestHiddenData id="campaign-ssp-deals-not-empty" />
          <Table aria-label="collapsible table" sx={{ border: theme => theme.palette.border, borderBottom: "none" }}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <FlexBox jc="start" gap>
                    <Typography color="textPrimary" fontSize=".95rem" fontWeight={600}>
                      {translate("ssp.list")}
                    </Typography>
                  </FlexBox>
                </TableCell>
                <TableCell width="1rem" />
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedSsps.map((ssp, index) => (
                <SspSspDealRow key={ssp.id} form={form} ssp={ssp} rowRenderIndex={rowRenderIndex} sspIndex={index} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </ScreensBoxLayout>
  )
})
